/**
 * Correlation ID Provider
 * 
 * Provides robust correlation ID generation and extraction from request headers.
 * Supports UUID-based generation with collision avoidance and request lifecycle management.
 */

/**
 * Correlation ID generation options
 */
export interface CorrelationIdOptions {
  // Extract from request headers if available
  extractFromHeaders?: boolean;
  // Header names to check for existing correlation ID
  headerNames?: string[];
  // Custom prefix for generated IDs
  prefix?: string;
  // Use crypto.randomUUID() if available, fallback to custom UUID generator
  useNativeUUID?: boolean;
  // Include timestamp in the correlation ID
  includeTimestamp?: boolean;
}

/**
 * Correlation ID context for request lifecycle management
 */
export interface CorrelationIdContext {
  correlationId: string;
  source: 'generated' | 'header' | 'custom';
  timestamp: number;
  requestHeaders?: Record<string, string>;
}

/**
 * Default correlation ID options
 */
const DEFAULT_OPTIONS: Required<CorrelationIdOptions> = {
  extractFromHeaders: true,
  headerNames: [
    'x-correlation-id',
    'x-request-id', 
    'x-trace-id',
    'correlation-id',
    'request-id',
    'trace-id'
  ],
  prefix: 'ulog',
  useNativeUUID: true,
  includeTimestamp: false
};

/**
 * Correlation ID Provider class
 */
export class CorrelationIdProvider {
  private options: Required<CorrelationIdOptions>;
  private currentContext: CorrelationIdContext | null = null;
  
  constructor(options: CorrelationIdOptions = {}) {
    this.options = { ...DEFAULT_OPTIONS, ...options };
  }
  
  /**
   * Generate a new UUID using crypto.randomUUID() or fallback
   */
  private generateUUID(): string {
    if (this.options.useNativeUUID && typeof crypto !== 'undefined' && crypto.randomUUID) {
      try {
        return crypto.randomUUID();
      } catch (error) {
        // Fallback to custom UUID generation
      }
    }
    
    // Custom UUID v4 generation (RFC 4122 compliant)
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
      const r = Math.random() * 16 | 0;
      const v = c === 'x' ? r : (r & 0x3 | 0x8);
      return v.toString(16);
    });
  }
  
  /**
   * Generate a timestamp-based component
   */
  private generateTimestamp(): string {
    return Date.now().toString(36);
  }
  
  /**
   * Generate a new correlation ID
   */
  private generateNewCorrelationId(): string {
    const uuid = this.generateUUID();
    const timestamp = this.options.includeTimestamp ? `_${this.generateTimestamp()}` : '';
    
    return `${this.options.prefix}_${uuid}${timestamp}`;
  }
  
  /**
   * Extract correlation ID from request headers
   */
  private extractFromHeaders(headers: Record<string, string> = {}): string | null {
    if (!this.options.extractFromHeaders) {
      return null;
    }
    
    // Convert headers to lowercase for case-insensitive matching
    const lowerHeaders = Object.keys(headers).reduce((acc, key) => {
      acc[key.toLowerCase()] = headers[key];
      return acc;
    }, {} as Record<string, string>);
    
    // Check each header name in order of preference
    for (const headerName of this.options.headerNames) {
      const value = lowerHeaders[headerName.toLowerCase()];
      if (value && typeof value === 'string' && value.trim().length > 0) {
        return value.trim();
      }
    }
    
    return null;
  }
  
  /**
   * Validate correlation ID format
   */
  private validateCorrelationId(correlationId: string): boolean {
    if (!correlationId || typeof correlationId !== 'string') {
      return false;
    }
    
    // Basic validation: non-empty, reasonable length, printable characters
    const trimmed = correlationId.trim();
    return trimmed.length > 0 && 
           trimmed.length <= 128 && 
           /^[a-zA-Z0-9_-]+$/.test(trimmed);
  }
  
  /**
   * Get or create correlation ID with optional request headers
   */
  public getCorrelationId(
    customId?: string,
    requestHeaders?: Record<string, string>
  ): CorrelationIdContext {
    const timestamp = Date.now();
    
    // If a custom ID is provided and valid, use it
    if (customId && this.validateCorrelationId(customId)) {
      const context: CorrelationIdContext = {
        correlationId: customId.trim(),
        source: 'custom',
        timestamp,
        requestHeaders
      };
      this.currentContext = context;
      return context;
    }
    
    // Try to extract from request headers
    if (requestHeaders) {
      const extractedId = this.extractFromHeaders(requestHeaders);
      if (extractedId && this.validateCorrelationId(extractedId)) {
        const context: CorrelationIdContext = {
          correlationId: extractedId,
          source: 'header',
          timestamp,
          requestHeaders
        };
        this.currentContext = context;
        return context;
      }
    }
    
    // Generate a new correlation ID
    const generatedId = this.generateNewCorrelationId();
    const context: CorrelationIdContext = {
      correlationId: generatedId,
      source: 'generated',
      timestamp,
      requestHeaders
    };
    
    this.currentContext = context;
    return context;
  }
  
  /**
   * Get current correlation ID context
   */
  public getCurrentContext(): CorrelationIdContext | null {
    return this.currentContext;
  }
  
  /**
   * Clear current context (typically at end of request)
   */
  public clearContext(): void {
    this.currentContext = null;
  }
  
  /**
   * Create a child correlation ID for nested operations
   */
  public createChildCorrelationId(parentId?: string): string {
    const parent = parentId || this.currentContext?.correlationId || this.generateNewCorrelationId();
    const childSuffix = Math.random().toString(36).substr(2, 4);
    return `${parent}.${childSuffix}`;
  }
  
  /**
   * Update provider options
   */
  public updateOptions(options: Partial<CorrelationIdOptions>): void {
    this.options = { ...this.options, ...options };
  }
  
  /**
   * Get current configuration
   */
  public getOptions(): Required<CorrelationIdOptions> {
    return { ...this.options };
  }
}

/**
 * Default correlation ID provider instance
 */
export const defaultCorrelationIdProvider = new CorrelationIdProvider();

/**
 * Convenience function to get correlation ID using default provider
 */
export function getCorrelationId(
  customId?: string,
  requestHeaders?: Record<string, string>
): string {
  return defaultCorrelationIdProvider.getCorrelationId(customId, requestHeaders).correlationId;
}

/**
 * Convenience function to get correlation ID context using default provider
 */
export function getCorrelationIdContext(
  customId?: string,
  requestHeaders?: Record<string, string>
): CorrelationIdContext {
  return defaultCorrelationIdProvider.getCorrelationId(customId, requestHeaders);
}

/**
 * Create a new correlation ID provider with custom options
 */
export function createCorrelationIdProvider(options: CorrelationIdOptions = {}): CorrelationIdProvider {
  return new CorrelationIdProvider(options);
} 